import { Dispatch } from "react-redux";
import { AuthActionCreators, IAddressValidationResponse, IApplicationState } from "..";
import { IListItem, IDonorShieldDirectCounts, IDonorShieldDirectAddEdit, IDashDonorShield, IDonorShieldDirectDuplicateCounts, DonorShieldAliasType } from "@nkr/models/index";
import { IDonorShieldDirectListResponse } from "@nkr/store/index";
import { IPost } from "../authorization";
import { OrganType } from "@nkr/enums/organ-type";

export interface IDonorShieldLookupDataResponse {
    data: {
        success: boolean,
        results: {
            stateList: IListItem[],
            insuranceList: IListItem[],
            organTypeList: IListItem[],
            isExpertSystemCenter: boolean
        }
    }
}

export interface IDonorShieldDirectResponse {
    data: {
        success: boolean,
        message: string,
        results: IDonorShieldDirectAddEdit,
        aliasCheck: IDonorShieldAliasCheck
    }
}

export interface IDashDonorExistingResponse {
    data: {
        success: boolean,
        message: string,
        results: IDashDonorShield[]
    }
}

export interface IResendRegistrationInviteResponse {
    data: {
        success: boolean,
        results: string
    }
}

export interface IDonorShieldDirectRequestCountsResponse {
    data: {
        success: boolean,
        results: IDonorShieldDirectCounts
    }
}

export interface IDonorShieldDirectDuplicateCountResponse {
    data: {
        success: boolean,
        results: IDonorShieldDirectDuplicateCounts
    }
}

export interface IDonorShieldDirectDataResponse {
    data: {
        success: boolean,
        results: any
    }
}

export interface IDonorShieldAliasCheckResponse {
    data: IDonorShieldAliasCheck
}

export interface IDonorShieldAliasCheck {
    success: boolean,
    alias: string,
    email: string,
    type: DonorShieldAliasType,
    message: string
}

export interface IDonorShieldDirectSaveRequest {
    id: number,
    centerId: number,
    surgeryDate: string,
    // donorId: number,
    donorUnosId: string,
    donorAlias: string,
    donorEmail: string,
    donorAboType: number,
    donorGender: number,
    donorName: string,
    donorAddressLine1: string,
    donorAddressLine2: string,
    donorCity: string,
    donorState: number,
    donorPostalCode: string,
    donorPhoneNumber: string,
    donorBirthdate: string,
    organType: number,
    recipientAlias: string,
    recipientEmail: string,
    recipientFirstName: string,
    recipientLastName: string,
    recipientAboType: number,
    recipientUnosCpra: string,
    recipientGender: number,
    recipientBirthdate: string,
    recipientPrimaryInsurance: number,
    recipientSecondaryInsurance: number,
}

export const ActionCreators = {
    getDonorShieldLookupData: (centerId: number, isAdmin: boolean) => async (dispatch: Dispatch<IApplicationState>): Promise<IDonorShieldLookupDataResponse> => {
        let options = {
            url: `${__API__}/donor-shield-direct/lookup-data?centerId=${centerId}`
        };

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },

    getDonorShieldDirectRecord: (donorShieldId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IDonorShieldDirectResponse> => {
        let options = {
            url: `${__API__}/donor-shield-direct/${donorShieldId}`
        };

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },

    getDonorShieldDirectList: (centerId: number, matchCategoryStatus: number, isAdmin: boolean, organType?: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IDonorShieldDirectListResponse> => {
        let options = {
            url: `${__API__}/donor-shield-direct/list?centerId=${centerId}&category=${matchCategoryStatus}`
        };

        if (organType != null) {
            options.url += `&organType=${organType}`;
        }

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },

    addDonorShieldDirectRecord: (dsdData: IDonorShieldDirectSaveRequest) => async (dispatch: Dispatch<IApplicationState>): Promise<IDonorShieldDirectResponse> => {
        let options: IPost = {
            url: `${__API__}/donor-shield-direct/add`,
            data: {
                centerId: dsdData.centerId,
                surgeryDate: dsdData.surgeryDate,
                donorUnosId: dsdData.donorUnosId,
                donorAlias: dsdData.donorAlias,
                donorEmail: dsdData.donorEmail,
                donorAboType: dsdData.donorAboType,
                donorGender: dsdData.donorGender,
                donorName: dsdData.donorName,
                donorAddressLine1: dsdData.donorAddressLine1,
                donorAddressLine2: dsdData.donorAddressLine2,
                donorCity: dsdData.donorCity,
                donorState: dsdData.donorState,
                donorPostalCode: dsdData.donorPostalCode,
                donorPhoneNumber: dsdData.donorPhoneNumber,
                donorBirthdate: dsdData.donorBirthdate,
                organType: dsdData.organType,
                recipientAlias: dsdData.recipientAlias,
                recipientEmail: dsdData.recipientEmail,
                recipientFirstName: dsdData.recipientFirstName,
                recipientLastName: dsdData.recipientLastName,
                recipientAboType: dsdData.recipientAboType,
                recipientUnosCpra: dsdData.recipientUnosCpra,
                recipientGender: dsdData.recipientGender,
                recipientBirthdate: dsdData.recipientBirthdate,
                recipientPrimaryInsurance: dsdData.recipientPrimaryInsurance,
                recipientSecondaryInsurance: dsdData.recipientSecondaryInsurance
            }
        };

        try {
            return await dispatch(AuthActionCreators.apiPost(options));
        } catch (error) {
            throw error;
        }
    },

    updateDonorShieldDirectRecord: (donorShieldId: number, dsdData: IDonorShieldDirectSaveRequest) => async (dispatch: Dispatch<IApplicationState>): Promise<IDonorShieldDirectResponse> => {
        let options: IPost = {
            url: `${__API__}/donor-shield-direct/${donorShieldId}`,
            data: {
                surgeryDate: dsdData.surgeryDate,
                donorUnosId: dsdData.donorUnosId,
                donorAlias: dsdData.donorAlias,
                donorEmail: dsdData.donorEmail,
                donorAboType: dsdData.donorAboType,
                donorGender: dsdData.donorGender,
                donorName: dsdData.donorName,
                donorAddressLine1: dsdData.donorAddressLine1,
                donorAddressLine2: dsdData.donorAddressLine2,
                donorCity: dsdData.donorCity,
                donorState: dsdData.donorState,
                donorPostalCode: dsdData.donorPostalCode,
                donorPhoneNumber: dsdData.donorPhoneNumber,
                donorBirthdate: dsdData.donorBirthdate,
                organType: dsdData.organType,
                recipientAlias: dsdData.recipientAlias,
                recipientEmail: dsdData.recipientEmail,
                recipientFirstName: dsdData.recipientFirstName,
                recipientLastName: dsdData.recipientLastName,
                recipientAboType: dsdData.recipientAboType,
                recipientUnosCpra: dsdData.recipientUnosCpra,
                recipientGender: dsdData.recipientGender,
                recipientBirthdate: dsdData.recipientBirthdate,
                recipientPrimaryInsurance: dsdData.recipientPrimaryInsurance,
                recipientSecondaryInsurance: dsdData.recipientSecondaryInsurance
            }
        };

        try {
            return await dispatch(AuthActionCreators.apiPut(options));
        } catch (error) {
            throw error;
        }
    },

    deleteDonorShieldDirectRecord: (Id: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IDonorShieldDirectListResponse> => {
        let options = {
            url: `${__API__}/donor-shield-direct/${Id}`
        };

        return await dispatch(AuthActionCreators.apiDelete(options));
    },

    getRequestCounts: (centerId: number, isAdmin: boolean, organType: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IDonorShieldDirectRequestCountsResponse> => {
        let url = ''

        if (centerId > 0) {
            url = `${__API__}/donor-shield-direct/request-counts?centerId=${centerId}`;
        } else if (isAdmin) {
            url = `${__API__}/admin/donor-shield-direct/request-counts`;
        }

        if (organType != null) {
            url += `&organType=${organType}`;
        }

        let options = {
            url: url
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    getDuplicateCounts: (donorShieldId: number, donorAlias: string, recipientAlias: string) => (dispatch: Dispatch<IApplicationState>): Promise<IDonorShieldDirectDuplicateCountResponse> => {
        let url = `${__API__}/donor-shield-direct/duplicate-counts?donorShieldId=${donorShieldId}&donorAlias=${encodeURIComponent(donorAlias)}&recipientAlias=${encodeURIComponent(recipientAlias)}`;

        let options = {
            url: url
        };

        return dispatch(AuthActionCreators.apiGet(options));
    },

    searchByAliases: (centerId: number, donorAlias: string, recipientAlias: string, organType: number) => (dispatch: Dispatch<IApplicationState>): Promise<any> => {
        const params = [
            `centerId=${centerId}`,
            `donorAlias=${encodeURIComponent(donorAlias || '')}`,
            `recipientAlias=${encodeURIComponent(recipientAlias || '')}`,
            `organType=${organType}`
        ].join('&');
        return dispatch(AuthActionCreators.apiGet({
            url: `${__API__}/donor-shield-direct/alias-search?${params}`
        }));
    },

    searchByPerson: (centerId: number, firstName: string, lastName: string, birthDate: string, organType: number) => (dispatch: Dispatch<IApplicationState>): Promise<any> => {
        const params = [
            `centerId=${centerId}`,
            `firstName=${encodeURIComponent(firstName || '')}`,
            `lastName=${encodeURIComponent(lastName || '')}`,
            `birthDate=${encodeURIComponent(birthDate || '')}`,
            `organType=${organType}`
        ].join('&');
        return dispatch(AuthActionCreators.apiGet({
            url: `${__API__}/donor-shield-direct/person-search?${params}`
        }));
    },

    getExistingRegistrations: (centerId: number, donorEmail: string) => async (dispatch: Dispatch<IApplicationState>): Promise<IDashDonorExistingResponse> => {
        let options = {
            url: `${__API__}/donor-shield-direct/existing-registrations?centerId=${centerId}&donorEmail=${encodeURIComponent(donorEmail)}`
        };

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },

    checkDonorShieldDirectAlias: (email: string, alias: string, type: DonorShieldAliasType, id: number, organType: OrganType) => async (dispatch: Dispatch<IApplicationState>): Promise<IDonorShieldAliasCheckResponse> => {
        let options = {
            url: `${__API__}/donor-shield-direct/check-existing-donor-alias`,
            data: {
                type,
                email,
                alias,
                id,
                organType
            }
        }

        try {
            return await dispatch(AuthActionCreators.apiPost(options))
        } catch (error) {
            throw error;
        }
    },

    resendRegistrationInvite: (donorShieldId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IResendRegistrationInviteResponse> => {
        let options: IPost = {
            url: `${__API__}/donor-shield-direct/${donorShieldId}/resend-invite`,
            data: {}
        };

        try {
            return await dispatch(AuthActionCreators.apiPost(options));
        } catch (error) {
            throw error;
        }
    },

    validateAddress: (address: string, address2: string, city: string, state: string, postalCode: string) => async (dispatch: Dispatch<IApplicationState>): Promise<IAddressValidationResponse> => {
        let options: IPost = {
            url: `${__API__}/donor-shield-direct/validate-address`,
            data: {
                address,
                address2,
                city,
                state,
                postalCode
            }
        }

        try {
            return await dispatch(AuthActionCreators.apiPost(options));
        } catch (error) {
            throw error;
        }
    },
};

